import {
  Circle,
  CircleCheck,
  Link2,
  ListPlus,
  Mail,
  PencilLine,
  Phone,
  Plus,
  Repeat,
  Send,
  StickyNote,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { formatDateTimeLong, formatRelatief } from '@/lib/format'
import type { ActivityKind, LastActivity } from '../query/types'
import { shortName } from './shortName'

const ICONS: Record<ActivityKind, LucideIcon> = {
  aangemaakt: Plus,
  profiel: PencilLine,
  lidmaatschap: Link2,
  notitie: StickyNote,
  gesprek: Phone,
  meeting: Users,
  email: Mail,
  mail_in: Mail,
  mail_uit: Send,
  taak: ListPlus,
  taak_afgerond: CircleCheck,
  afspraak: Repeat,
}

const AGO = ' geleden'

/**
 * "Laatste activiteit" (addendum A.5.1, ED-63): what the team last did with a relation, when
 * and who, as icon · kind · time · person · "via" the contact it came through. In a narrow
 * column the kind is only read aloud and " geleden" goes; the full moment is in `title` and
 * `aria-label` of the time. Synced sent mail has no person: it went out through the shared mailbox.
 *
 * The root is a size container, so it adds no width of its own to the automatic layout of a
 * table: the column gets only what its header and the spare width give it, and the kind and
 * " geleden" collapse. The column that shows this cell needs an explicit minimum width
 * (`min-w-60`, the ~240 px of addendum A.4.7).
 *
 * What still does not fit stays inside the cell, never over the next column: the kind, the person
 * and the "via" parts shorten with an ellipsis (their full text in `title`), the icon and the time
 * keep their width, and the root clips whatever is left.
 */
export function LastActivityCell({ activity, now }: { activity: LastActivity | null; now?: Date }) {
  if (!activity) {
    return (
      <span aria-label="Nog geen activiteit" className="text-muted-foreground">
        —
      </span>
    )
  }
  const Icon = ICONS[activity.kind] ?? Circle
  const relative = formatRelatief(activity.at, now)
  const ago = relative.endsWith(AGO)
  const moment = formatDateTimeLong(activity.at)
  return (
    <div className="@container flex min-w-0 items-center gap-1.5 overflow-hidden text-sm">
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 truncate @max-[12rem]:sr-only" title={activity.label}>
        {activity.label}
      </span>
      <time dateTime={activity.at} title={moment} aria-label={moment} className="shrink-0 text-muted-foreground">
        {ago ? relative.slice(0, -AGO.length) : relative}
        {ago ? <span className="@max-[15rem]:hidden">{AGO}</span> : null}
      </time>
      {activity.by ? (
        // Clipped too: shortened below the width of its avatar, the avatar would run into the "via" text.
        <span className="inline-flex min-w-0 items-center gap-1 overflow-hidden">
          <EntityAvatar kind="person" name={activity.by.full_name} size="xs" />
          <span className="truncate" title={activity.by.full_name}>
            {shortName(activity.by.full_name)}
          </span>
        </span>
      ) : activity.kind === 'mail_uit' ? (
        <span className="min-w-0 truncate text-muted-foreground" title="via info@">
          via info@
        </span>
      ) : null}
      {activity.via ? (
        <span className="min-w-0 truncate text-muted-foreground" title={`via ${activity.via.full_name}`}>
          via {shortName(activity.via.full_name)}
        </span>
      ) : null}
    </div>
  )
}
